/* Búsqueda múltiple de cartas — sección de tema Shopify, adaptada
   visualmente del kiosco Flutter (busqueda_multiple_screen.dart +
   fila_resultado_busqueda.dart) para que la UI se vea igual en el sitio
   web: grilla/lista, badges de estado, panel de variantes en cajas,
   botón "Quitar de lista", resumen con el mismo texto. Busca cada carta
   contra /search (Section Rendering) y agrega al carrito con la Cart
   AJAX API nativa del tema. Sin backend propio. */
(() => {
  'use strict';

  // ---- Parser de listas — port 1:1 del parser del kiosco
  // (backend-tcg-store/backend/app/services/decklist_parser.py:
  // parse_line/parse_list/detect_game), que es la versión más evolucionada
  // y ya validada en producción. MANTENER EN PARIDAD con ese archivo (el
  // viejo `referencia/busqueda-multiple.js` quedó atrás: no quitaba códigos
  // con guion "(OP11-041)", no separaba el subtítulo "|" de Star Wars y no
  // reconocía encabezados como Character/Event/Base/Monster).

  const RE_HEADER = /^(deck(list)?|ma[iz]n\s?(deck|board)?|mazo|side\s?board|side|banca|reserva|commander|comandante|companion|acompañante|pok[eé]mon|trainer|entrenador(es)?|energy|energ[ií]a(s)?|leader|l[ií]der|base|don!?!?|stage|extra\s?deck|extra|egg(s)?|huevo(s)?|character(s)?|personaje(s)?|event(s)?|evento(s)?|monster(s)?|monstruo(s)?|trap(s)?|trampa(s)?|spell(s)?|hechizo(s)?|total|about|name|nombre)\s*:?\s*(\(\d+\))?\s*\d*\s*$/i;
  const RE_HEADER_KV = /^(total|name|nombre|format(o)?|date|fecha|about|deck(list)?|autor|author|creator|event(o)?)\b[^:]*:.*$/i;
  const RE_TYPE_HEADER = /^(lands?|creatures?|instants?(\s+(and|y)\s+sorc\w*\.?)?|sorcer(y|ies)|artifacts?|enchantments?|planeswalkers?|battles?|(other\s+)?spells?|monsters?|monstruos?|traps?|trampas?|ma[iz]n\s?(deck|board)|side\s?board|extra\s?deck|cards?\s+total|total(\s+cards?)?|tierras?|criaturas?|conjuros?|instant[aá]neos?|encantamientos?|artefactos?|caminantes(\s+de\s+planos)?)$/i;
  const RE_PRICE = /^([$€£¥]\s*)?[\d.,]+\s*(usd|mxn|eur|tix)?\s*$/i;
  const RE_COMMENT = /^(\/\/|#|--)/;

  // Palabras de encabezado propias de cada juego — sirven para adivinar el
  // `product_type` de la lista y buscar SOLO en ese juego (sin esto,
  // "Judge" de Pokémon caía en una carta de Magic). "leader"/"lider" está
  // en One Piece y en Star Wars a propósito: si solo aparece eso queda
  // empate (null, se busca en todos los tipos).
  const GAME_TRIGGERS = [
    ['Pokemon Single', ['pokemon', 'trainer', 'energy', 'energia']],
    ['One Piece Single', ['leader', 'lider', 'character', 'personaje', 'event', 'evento', 'don']],
    ['Star Wars: Unlimited Single', ['leader', 'lider', 'base']],
    ['Yugioh Single', ['monster', 'monsters', 'monstruo', 'monstruos', 'trap', 'traps', 'trampa', 'trampas', 'fusion', 'synchro', 'xyz', 'link']],
    ['MTG Single', ['land', 'lands', 'creature', 'creatures', 'instant', 'instants', 'sorcery', 'sorceries', 'artifact', 'artifacts', 'enchantment', 'enchantments', 'planeswalker', 'planeswalkers', 'commander', 'comandante', 'companion', 'acompanante']],
    ['Riftbound Single', ['champion', 'champions', 'battlefield', 'battlefields', 'legend', 'legends', 'rune', 'runes', 'gear']],
  ];

  const CONCURRENCY = 3;
  const MAX_LINES = 200;
  const MAX_CANDIDATE_PRODUCTS = 5; // tope de productos distintos traídos por carta buscada
  const MAX_VARIANT_BOXES = 6; // tope de cajas visibles en el panel de variantes

  function normalize(str) {
    return str
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^\w\s-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  // Sin señales o empate entre juegos -> null (se busca en todos los tipos).
  function detectGame(text) {
    const puntajes = new Map(GAME_TRIGGERS.map(([nombre]) => [nombre, 0]));
    for (const raw of text.split(/\r\n|\r|\n/)) {
      // Solo encabezados ("Energy: 13", "11 CREATURES", "Character"), nunca
      // líneas de carta: "1 Instill Energy" (una carta de Magic) contaba como
      // Pokémon y acotaba TODA la lista a Pokémon — 0 resultados
      // (2026-10-26). Una línea es carta si parseLine la convierte en
      // entrada; mismo criterio en decklist_parser.detect_game (kiosco).
      if (parseLine(raw)) continue;
      const palabras = new Set(normalize(raw).split(' '));
      for (const [nombre, disparadores] of GAME_TRIGGERS) {
        if (disparadores.some((d) => palabras.has(d))) puntajes.set(nombre, puntajes.get(nombre) + 1);
      }
    }
    const mejor = Math.max(...puntajes.values());
    if (mejor === 0) return null;
    const ganadores = [...puntajes].filter(([, p]) => p === mejor).map(([n]) => n);
    return ganadores.length === 1 ? ganadores[0] : null;
  }

  function parseLine(raw) {
    let line = raw.trim();
    if (!line || RE_COMMENT.test(line) || RE_HEADER.test(line) || RE_HEADER_KV.test(line) || RE_PRICE.test(line)) return null;

    let qty = 1;
    let m = line.match(/^(\d+)\s*[xX]?\s+(.+)$/);
    if (m) {
      qty = parseInt(m[1], 10);
      line = m[2];
    } else if ((m = line.match(/^(.+?)\s+[xX](\d+)$/))) {
      qty = parseInt(m[2], 10);
      line = m[1];
    }

    line = line.replace(/\s*\*[A-Za-z]\*\s*$/, '');

    // Código de set al final — "(M21) 234", "(OP11-041)", "SVI 25": se quita
    // del nombre (Shopify no lo trae en el título) pero se conserva para el
    // dedupe: mismo nombre con distinto código = cartas distintas en One
    // Piece/Pokémon (un Líder y un Personaje pueden compartir nombre).
    let code = null;
    m = line.match(/\s*\(([\w-]{2,15})\)\s*([\w-]*)$/);
    if (m) {
      code = (m[1] + (m[2] || '')).toLowerCase();
      line = line.slice(0, m.index).trimEnd();
    } else if ((m = line.match(/\s+([A-Z]{2,4}\s+\d{1,3}[a-zA-Z]?)$/))) {
      code = m[1].replace(/\s+/g, '').toLowerCase();
      line = line.slice(0, m.index).trimEnd();
    }

    line = line.split(' // ')[0].trim(); // cartas dobles de Magic
    // Subtítulo de Star Wars: Unlimited — "R2-D2 | Artooooooooo!": se busca
    // solo el personaje, pero el subtítulo se conserva porque distingue
    // cartas distintas ("R2-D2 | Part of the Plan" es otra carta).
    const partes = line.split(/\s*\|\s*/);
    line = partes[0].trim();
    const subtitle = partes.length > 1 && partes.slice(1).join(' | ').trim() ? partes.slice(1).join(' | ').trim() : null;

    if (!line || /^[\d\s().,-]+$/.test(line) || RE_TYPE_HEADER.test(line)) return null;
    return { qty: Math.min(Math.max(qty, 1), 99), name: line, raw: raw.trim(), code, subtitle };
  }

  function parseList(text) {
    const seen = new Map();
    let truncated = false;
    for (const raw of text.split(/\r\n|\r|\n/)) {
      const entry = parseLine(raw);
      if (!entry) continue;
      const { code } = entry;
      delete entry.code;
      let base = normalize(entry.name);
      if (entry.subtitle) base = `${base}|${normalize(entry.subtitle)}`;
      const key = code ? `${base}|${code}` : base;
      if (seen.has(key)) {
        seen.get(key).qty = Math.min(seen.get(key).qty + entry.qty, 99);
      } else if (seen.size >= MAX_LINES) {
        truncated = true;
      } else {
        seen.set(key, entry);
      }
    }
    const entries = [...seen.values()];
    entries.truncated = truncated;
    entries.detectedGame = detectGame(text);
    return entries;
  }

  const clampQty = (n) => Math.min(Math.max(parseInt(n, 10) || 0, 0), 99);

  // `quantity === null` = no rastrea inventario, vendible sin tope (nunca
  // "agotado"). `available` ya refleja la política de venta de Shopify.
  const tieneStock = (v) => v.available && (v.quantity === null || v.quantity > 0);

  // ---- Coincidencia de nombre — port 1:1 de `puntajeBusquedaProducto`
  // del kiosco (lib/utils/product_search_match.dart). MANTENER EN PARIDAD.
  // Además del nombre exacto, acepta el nombre seguido de parte de la
  // edición/variante: "naturalize 2011" -> "Naturalize [Magic 2011]",
  // "shock turtles" -> "Shock [Teenage Mutant Ninja Turtles]" (y nunca
  // "Shellshock"), "sol ring retro" -> "... (Retro Frame)".
  const normalizarBusqueda = (t) => t.toLowerCase().trim();
  const nombreCarta = (tituloNorm) => tituloNorm.split(/[[(]/)[0].trim();
  const tokens = (t) => new Set(t.split(/[^a-z0-9áéíóúüñ]+/).filter(Boolean));

  function puntajeBusquedaProducto(tituloProducto, buscado) {
    const titulo = normalizarBusqueda(tituloProducto);
    const consulta = normalizarBusqueda(buscado);
    const nombre = nombreCarta(titulo);

    if (nombre === consulta) return 4;
    if (titulo === consulta) return 3;

    if (consulta.startsWith(`${nombre} `)) {
      const buscados = tokens(consulta.substring(nombre.length).trim());
      const calificadores = tokens(titulo.substring(nombre.length));
      if (buscados.size && [...buscados].every((t) => calificadores.has(t))) return 4;
    }

    if (nombre.startsWith(consulta) || titulo.startsWith(consulta)) return 2;
    if (titulo.includes(consulta)) return 1;
    return 0;
  }

  const ICON_OK = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-4px" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="m8.5 12.5 2.5 2.5 5-6"/></svg>';
  const ICON_WARN = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-4px" aria-hidden="true"><path d="M12 3 2.5 20h19L12 3z"/><path d="M12 9.5v4.5m0 3v.01"/></svg>';
  const ICON_MINUS = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>';
  const ICON_PLUS = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
  const ICON_X_SMALL = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
  const ICON_ALERT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3 2.5 20h19L12 3z"/><path d="M12 9.5v4.5m0 3v.01"/></svg>';

  class BusquedaMultiple extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('.bm__form');
      this.textarea = this.querySelector('.bm__textarea');
      this.allowedTypes = (this.dataset.allowedTypes || '')
        .split(',').map((s) => s.trim()).filter(Boolean);
      this.statusEl = this.querySelector('.bm__status');
      this.resultsEl = this.querySelector('.bm__results');
      this.list = this.querySelector('.bm__list');
      this.grid = this.querySelector('.bm__grid');
      this.resultsCount = this.querySelector('.bm__results-count');
      this.notfoundSection = this.querySelector('.bm__notfound-section');
      this.notfoundHeading = this.querySelector('.bm__notfound-heading');
      this.notfoundList = this.querySelector('.bm__notfound-list');
      this.summaryText = this.querySelector('.bm__footer-text');
      this.summaryTotal = this.querySelector('.bm__footer-total');
      this.footerWarning = this.querySelector('.bm__footer-warning');
      this.cartMessage = this.querySelector('.bm__cart-message');
      this.addButton = this.querySelector('.bm__add-to-cart');
      this.modal = this.querySelector('.bm__modal');
      this.modalBody = this.querySelector('.bm__modal-body');
      this.showStock = this.dataset.showStock === 'true';
      this.viewMode = 'list';

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.search();
      });
      this.querySelector('.bm__clear').addEventListener('click', () => {
        this.textarea.value = '';
        this.resultsEl.hidden = true;
        this.cartMessage.hidden = true;
        this.setStatus('');
        this.entries = [];
      });
      this.querySelector('.bm__edit-list-link').addEventListener('click', () => {
        this.textarea.focus();
        this.textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      this.querySelectorAll('.bm__view-btn').forEach((btn) => {
        btn.addEventListener('click', () => this.setViewMode(btn.dataset.view));
      });
      this.addButton.addEventListener('click', () => this.onAddToCartClick());
      this.list.addEventListener('click', (e) => this.onRowClick(e));
      this.notfoundList.addEventListener('click', (e) => {
        const z = e.target.closest('[data-action="zoom"]');
        if (z) this.openLightbox(z.dataset.src);
      });
      this.lightbox = this.querySelector('.bm__lightbox');
      this.lightboxImg = this.querySelector('.bm__lightbox-img');
      this.lightbox.addEventListener('click', () => this.closeLightbox());
      this.addEventListener('keydown', (e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target.matches('[data-action="zoom"]')) {
          e.preventDefault();
          this.openLightbox(e.target.dataset.src);
        }
      });
      this.list.addEventListener('input', (e) => this.onRowInput(e));
      this.grid.addEventListener('click', (e) => this.onGridClick(e));
      this.grid.addEventListener('input', (e) => this.onGridClick(e));
      this.modal.addEventListener('click', (e) => this.onModalClick(e));
      this.querySelector('.bm__modal-close').addEventListener('click', () => this.closeModal());
    }

    setStatus(text) {
      this.statusEl.hidden = !text;
      this.statusEl.textContent = text || '';
    }

    setViewMode(mode) {
      this.viewMode = mode;
      this.querySelectorAll('.bm__view-btn').forEach((btn) => {
        btn.setAttribute('aria-pressed', String(btn.dataset.view === mode));
      });
      this.list.hidden = mode !== 'list';
      this.grid.hidden = mode !== 'grid';
      this.render();
    }

    async search() {
      const entries = parseList(this.textarea.value);
      this.detectedGame = entries.detectedGame;
      this.cartMessage.hidden = true;
      if (!entries.length) {
        this.setStatus('No reconocí ninguna carta en la lista. Escribe una carta por línea, por ejemplo: "4 Lightning Bolt".');
        this.resultsEl.hidden = true;
        return;
      }

      const button = this.form.querySelector('button[type="submit"]');
      button.disabled = true;
      let done = 0;
      this.setStatus(`Buscando ${entries.length} cartas… (0/${entries.length})`);

      const queue = [...entries];
      let consecutiveErrors = 0;
      let aborted = false;
      const worker = async () => {
        while (queue.length && !aborted) {
          const entry = queue.shift();
          const aplicar = (res) => {
            entry.products = res.products;
            entry.productoSinStock = res.sinStock;
          };
          try {
            aplicar(await this.fetchProducts(entry.name, entry.subtitle));
            consecutiveErrors = 0;
          } catch (err) {
            try {
              await new Promise((r) => setTimeout(r, 1200));
              aplicar(await this.fetchProducts(entry.name, entry.subtitle));
              consecutiveErrors = 0;
            } catch (err2) {
              entry.error = true;
              entry.products = [];
              consecutiveErrors += 1;
              if (consecutiveErrors >= 3) aborted = true;
            }
          }
          done += 1;
          this.setStatus(`Buscando ${entries.length} cartas… (${done}/${entries.length})`);
        }
      };
      await Promise.all(Array.from({ length: CONCURRENCY }, worker));

      entries.forEach((e) => {
        if (!e.products) { e.products = []; e.error = true; }
        e.picks = new Map();
        e.touched = false;
        e.manualIncluida = true;
        e.showAllVariants = false;
        this.autoAssign(e);
      });

      button.disabled = false;
      if (aborted) {
        this.setStatus('No se pudo consultar el inventario en este momento. Intenta de nuevo en unos segundos.');
      } else if (entries.truncated) {
        this.setStatus(`Se procesaron las primeras ${MAX_LINES} cartas de la lista; el resto se omitió.`);
      } else {
        this.setStatus('');
      }
      this.entries = entries;
      this.render();
    }

    // /search con operador `title:` (SIN comodines — confirmado en vivo
    // 2026-10-15, `title:*term*` está mal formado para Shopify) en vez de
    // Predictive Search: hasta 50 candidatos reales por carta (vs. 10).
    //
    // Primero la frase exacta entre comillas: sin comillas Shopify aplica
    // `title:` solo a la PRIMERA palabra y el resto cuenta como palabras
    // sueltas — "Captain Marvel, Earth's Protector" regresaba 50 "Captain
    // America" y la carta real nunca llegaba (2026-10-20). Si la frase no da
    // una coincidencia válida (con o sin stock) se repite sin comillas, para
    // títulos de Shopify que no traen el nombre tal cual.
    async fetchProducts(name, subtitle = null) {
      const term = name.replace(/["*]/g, '').trim();
      const exacta = this.elegir(await this.consultar(`title:"${term}"`), name, subtitle);
      if (exacta.products.length || exacta.sinStock) return exacta;
      return this.elegir(await this.consultar(`title:${term}`), name, subtitle);
    }

    async consultar(q) {
      const params = new URLSearchParams({
        q,
        type: 'product',
        sections: this.dataset.datosSection,
      });
      const res = await fetch(`${this.dataset.searchUrl}?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const wrapper = await res.json();
      // La Section Rendering API SIEMPRE envuelve el HTML de la sección en
      // <div id="shopify-section-{id}" class="shopify-section">...</div>,
      // incluso cuando la sección solo emite JSON — hay que extraer el
      // texto de adentro antes de parsear.
      const rawHtml = wrapper[this.dataset.datosSection];
      let data = { products: [] };
      if (rawHtml) {
        const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
        const jsonText = doc.body.textContent.trim();
        if (jsonText) data = JSON.parse(jsonText);
      }
      let products = data.products || [];
      if (this.allowedTypes.length) {
        products = products.filter((p) => this.allowedTypes.includes(p.type));
      }
      // Igual que el kiosco: si la lista deja claro de qué juego es, solo se
      // consideran cartas de ese juego.
      if (this.detectedGame) {
        products = products.filter((p) => p.type === this.detectedGame);
      }
      return products;
    }

    elegir(products, name, subtitle) {
      // Con subtítulo (Star Wars: Unlimited) solo cuentan los productos que
      // lo traen en el título, y se califica contra "nombre - subtítulo"
      // (así se titulan en Shopify); si ningún título tiene esa forma, se cae
      // al nombre solo. Igual que `_resolverFila` del kiosco.
      const sub = subtitle ? normalizarBusqueda(subtitle) : '';
      if (sub) products = products.filter((p) => normalizarBusqueda(p.title).includes(sub));
      let buscado = name;
      if (sub) {
        const conSubtitulo = `${name} - ${subtitle.trim()}`;
        if (products.some((p) => puntajeBusquedaProducto(p.title, conSubtitulo) >= 2)) buscado = conSubtitulo;
      }
      // Igual que `_resolverFila` del kiosco: primero se decide CUÁL carta es
      // (el mejor puntaje entre TODAS las coincidencias, tengan o no stock) y
      // solo se aceptan las del mismo nivel. Puntaje 1 (el texto aparece en
      // algún lado del título) no basta — p. ej. "seam rip" no debe aceptar
      // "Threadbind Clique // Rip the Seams". El orden de relevancia de
      // Shopify desempata.
      const calificados = products
        .map((p, i) => ({ p, i, score: puntajeBusquedaProducto(p.title, buscado) }))
        .sort((a, b) => (b.score - a.score) || (a.i - b.i));
      const mejorScore = calificados.length ? calificados[0].score : 0;
      if (mejorScore < 2) return { products: [], sinStock: null };
      const mismoNivel = calificados.filter((x) => x.score === mejorScore).map((x) => x.p);
      // Igual que el kiosco (`soloConStock` / `VarianteBusqueda.tieneStock`):
      // solo se ofrecen variantes con existencia; un producto que se queda
      // sin variantes se descarta. Si ninguna del mismo nivel tiene stock, se
      // conserva la primera para mostrar "No disponible" con su imagen.
      const conStock = mismoNivel
        .map((p) => ({ ...p, variants: p.variants.filter(tieneStock) }))
        .filter((p) => p.variants.length);
      return {
        products: conStock.slice(0, MAX_CANDIDATE_PRODUCTS),
        sinStock: conStock.length ? null : mismoNivel[0],
      };
    }


    // Asigna automáticamente la cantidad solicitada al mejor candidato con
    // existencia (o al primero si ninguno tiene). El cliente puede
    // redistribuir manualmente entre ediciones/condiciones abriendo el panel.
    autoAssign(entry) {
      if (!entry.products.length) return;
      let chosen = null;
      outer: for (let pi = 0; pi < entry.products.length; pi++) {
        const variants = entry.products[pi].variants;
        for (let vi = 0; vi < variants.length; vi++) {
          const v = variants[vi];
          if (v.available && (v.quantity === null || v.quantity > 0)) {
            chosen = { pi, vi, variant: v };
            break outer;
          }
        }
      }
      if (!chosen) return; // sin ninguna variante disponible — queda en "no disponible"
      let qty = entry.qty;
      if (chosen.variant.quantity !== null && chosen.variant.quantity < qty) {
        qty = chosen.variant.quantity;
      }
      if (qty > 0) entry.picks.set(`${chosen.pi}:${chosen.vi}`, qty);
    }

    totalPicked(entry) {
      let sum = 0;
      entry.picks.forEach((qty) => { sum += qty; });
      return sum;
    }

    // Candidatos (producto, variante) planos, ordenados: ya elegidos primero,
    // luego los que tienen existencia, luego el resto.
    candidateList(entry) {
      const list = [];
      entry.products.forEach((product, pi) => {
        product.variants.forEach((variant, vi) => {
          list.push({ pi, vi, product, variant, picked: entry.picks.has(`${pi}:${vi}`) });
        });
      });
      list.sort((a, b) => {
        if (a.picked !== b.picked) return a.picked ? -1 : 1;
        const aOk = a.variant.available ? 1 : 0;
        const bOk = b.variant.available ? 1 : 0;
        return bOk - aOk;
      });
      return list;
    }

    statusFor(entry) {
      if (!entry.products.length) {
        const label = entry.error ? 'Error al buscar' : (entry.productoSinStock ? 'No disponible' : 'No encontrado');
        return { kind: 'notfound', label };
      }
      const total = this.totalPicked(entry);
      if (total === 0) return { kind: 'badge', cls: 'neutral', text: 'Sin versión elegida' };
      if (total >= entry.qty) return { kind: 'badge', cls: 'ok', text: `Cubiertas ${total} de ${entry.qty}` };
      const candidates = this.candidateList(entry);
      const moreAvailable = candidates.some((c) => !c.picked && c.variant.available && (c.variant.quantity === null || c.variant.quantity > 0));
      return {
        kind: 'badge',
        cls: 'warn',
        text: `Seleccionadas ${total} de ${entry.qty} solicitadas${moreAvailable ? ' — hay más versiones disponibles' : ''}`,
      };
    }

    isIncluida(entry) {
      const total = this.totalPicked(entry);
      if (total === 0) return false;
      return entry.touched ? entry.manualIncluida : true;
    }

    render() {
      if (!this.entries) return;
      const found = this.entries.filter((e) => e.products.length);
      const notFound = this.entries.filter((e) => !e.products.length);

      this.resultsCount.textContent = `${found.length} de ${this.entries.length} cartas encontradas`;

      if (this.viewMode === 'grid') {
        this.renderGrid(found);
      } else {
        this.renderList(found);
      }
      this.renderNotFound(notFound);
      this.resultsEl.hidden = false;
      this.updateFooter();
    }

    renderList(found) {
      this.list.innerHTML = '';
      found.forEach((entry) => {
        const i = this.entries.indexOf(entry);
        const li = document.createElement('div');
        li.className = `bm__card bm__row${this.isIncluida(entry) ? '' : ' bm__card--dim'}`;
        li.dataset.index = i;
        li.innerHTML = this.renderRow(entry);
        this.list.appendChild(li);
      });
    }

    renderRow(entry, options) {
      const opts = options || {};
      const status = this.statusFor(entry);
      const badgeHtml = status.kind === 'badge'
        ? `<span class="bm__badge bm__badge--${status.cls}">${this.escape(status.text)}</span>`
        : '';
      const candidates = this.candidateList(entry);
      const picked = candidates.filter((c) => c.picked);
      const title = picked.length === 1 ? picked[0].product.title : entry.name;
      const subtitle = picked.length === 1
        ? `Edición · Versión: <strong>${this.escape(picked[0].variant.title)}</strong>`
        : (entry.products[0] ? `${entry.products.length} edición(es) encontradas` : '');
      const thumbSrc = picked[0] ? picked[0].product.image : (entry.products[0] ? entry.products[0].image : null);
      const thumbProduct = picked[0] ? picked[0].product : entry.products[0];
      const thumbHtml = this.thumbHtml(thumbSrc, entry.name, 'bm__thumb bm__row-thumb', thumbProduct && (thumbProduct.image_large || thumbProduct.image));

      let middleHtml = '';
      if (picked.length === 1) {
        const c = picked[0];
        const qty = entry.picks.get(`${c.pi}:${c.vi}`);
        middleHtml = this.stepperHtml(c.pi, c.vi, qty);
      } else if (picked.length > 1) {
        middleHtml = `<span class="bm__chip-neutral">${this.totalPicked(entry)} en total</span>`;
      }

      // Igual que el kiosco: el precio de la fila es el TOTAL de lo elegido
      // (precio × cantidad, sumado entre ediciones si se repartió); el
      // subtexto da el precio unitario o cuántas versiones se combinaron.
      let priceHtml = '<span class="bm__price-current">—</span>';
      if (picked.length) {
        const totalCents = picked.reduce((sum, c) => sum + (c.variant.price || 0) * (entry.picks.get(`${c.pi}:${c.vi}`) || 0), 0);
        priceHtml = `<span class="bm__price-current">${this.escape(this.formatMoney(totalCents))}</span>`;
        if (picked.length > 1) {
          priceHtml += `<span class="bm__price-unit">${picked.length} versiones</span>`;
        } else if (this.totalPicked(entry) > 1) {
          priceHtml += `<span class="bm__price-unit">${this.escape(picked[0].variant.price_formatted)} c/u</span>`;
        }
      }

      const candidateCount = entry.products.reduce((s, p) => s + p.variants.length, 0);
      const variantsLink = candidateCount > 1
        ? `<button type="button" class="bm__row-variants-link" data-action="toggle-variants">${entry.showVariants ? 'Ocultar variantes' : `Ver variantes (${candidateCount})`}</button>`
        : '';

      const checked = this.isIncluida(entry) ? 'checked' : '';
      const disabled = this.totalPicked(entry) === 0 ? 'disabled' : '';

      // En el modal ya hay un botón "×" para cerrar, en la misma esquina —
      // mostrar también "Quitar de lista" ahí confundía el cierre con el
      // borrado (confirmado en vivo 2026-10-15, los dos quedaban pegados).
      const removePill = opts.hideRemove ? '' : `<button type="button" class="bm__remove-pill" data-action="remove">${ICON_X_SMALL}Quitar de lista</button>`;

      return `
        ${removePill}
        <div class="bm__row-main">
          ${thumbHtml}
          <div class="bm__row-info">
            <div class="bm__row-title">${this.escape(title)}</div>
            <div class="bm__row-subtitle">${subtitle}</div>
            <div class="bm__row-raw">${this.escape(entry.raw)}</div>
            ${variantsLink}
          </div>
          <div class="bm__row-status">${badgeHtml}</div>
          <div class="bm__row-qty">${middleHtml}</div>
          <div class="bm__row-price">${priceHtml}</div>
          <label class="bm__row-include">
            <input type="checkbox" data-action="include" ${checked} ${disabled}>
            Incluir
          </label>
        </div>
        ${entry.showVariants ? this.variantsPanelHtml(entry, candidates) : ''}`;
    }

    variantsPanelHtml(entry, candidates) {
      // Orden fijo mientras el panel siga abierto (pedido 2026-10-23): antes
      // las tarjetas se reordenaban en cada render con las elegidas primero,
      // así que al tocar "Agregar" la tarjeta (y su cuadro de cantidad)
      // saltaba de lugar bajo el dedo. El orden "elegidas primero" se toma
      // una sola vez, al abrir el panel.
      if (!entry.variantOrder) entry.variantOrder = candidates.map((c) => `${c.pi}:${c.vi}`);
      const pos = new Map(entry.variantOrder.map((key, i) => [key, i]));
      const rank = (c) => (pos.has(`${c.pi}:${c.vi}`) ? pos.get(`${c.pi}:${c.vi}`) : Number.MAX_SAFE_INTEGER);
      candidates = [...candidates].sort((a, b) => rank(a) - rank(b));
      const visible = entry.showAllVariants ? candidates : candidates.slice(0, MAX_VARIANT_BOXES);
      const boxes = visible.map((c) => this.variantBoxHtml(entry, c)).join('');
      const toggle = candidates.length > MAX_VARIANT_BOXES
        ? `<button type="button" class="bm__row-variants-link" data-action="toggle-show-all">${entry.showAllVariants ? 'Mostrar menos' : `Mostrar todas (${candidates.length})`}</button>`
        : '';
      const multiEdition = new Set(entry.products.map((p) => p.id)).size > 1;
      return `
        <div class="bm__variants-panel">
          <div class="bm__variants-heading">${multiEdition ? 'Variantes y condición' : 'Condición'}</div>
          <div class="bm__variants-grid">${boxes}</div>
          ${toggle}
        </div>`;
    }

    // La miniatura de cada tarjeta también abre el visor en grande (pedido
    // 2026-10-23), igual que la de la fila. En una tarjeta sin elegir (que es
    // un <button> "Agregar"), tocar la imagen solo abre el visor: handleAction
    // toma el `data-action` más cercano, que es el `zoom` de la imagen.
    variantBoxHtml(entry, c) {
      const { pi, vi, product, variant, picked } = c;
      const multiEdition = new Set(entry.products.map((p) => p.id)).size > 1;
      const editionHtml = multiEdition ? `<div class="bm__variant-box-edition">${this.escape(product.title)}</div>` : '';
      const stockLabel = this.stockLabel(variant);
      const priceHtml = this.priceHtml(variant, 1);
      if (picked) {
        const qty = entry.picks.get(`${pi}:${vi}`);
        return `
          <div class="bm__variant-box bm__variant-box--picked" data-pi="${pi}" data-vi="${vi}">
            ${this.thumbHtml(variant.image || product.image, product.title, 'bm__thumb bm__variant-box-thumb', product.image_large || product.image)}
            <div class="bm__variant-box-body">
              <div class="bm__variant-box-condition">${this.escape(variant.title)}</div>
              ${editionHtml}
              <div class="bm__variant-box-price">${priceHtml}</div>
              <div class="bm__variant-box-stepper">${this.stepperHtml(pi, vi, qty)}</div>
            </div>
          </div>`;
      }
      const canAdd = variant.available;
      return `
        <button type="button" class="bm__variant-box" data-pi="${pi}" data-vi="${vi}" ${canAdd ? 'data-action="add-variant"' : 'disabled'}>
          ${this.thumbHtml(variant.image || product.image, product.title, 'bm__thumb bm__variant-box-thumb', product.image_large || product.image)}
          <div class="bm__variant-box-body">
            <div class="bm__variant-box-condition">${this.escape(variant.title)}</div>
            ${editionHtml}
            <div class="bm__variant-box-price">${priceHtml}</div>
            <div class="bm__variant-box-stock">${stockLabel}</div>
            ${canAdd ? '<span class="bm__variant-box-add">Agregar</span>' : ''}
          </div>
        </button>`;
    }

    stockLabel(variant) {
      if (!variant.available) return 'Agotado';
      if (variant.quantity === null) return 'Disponible';
      if (variant.quantity <= 0) return 'Bajo pedido';
      return this.showStock ? `Disponible (${variant.quantity})` : 'Disponible';
    }

    stepperHtml(pi, vi, qty) {
      return `
        <span class="bm__row-stepper">
          <button type="button" class="bm__stepper-btn" data-action="qty-minus" data-pi="${pi}" data-vi="${vi}" aria-label="Quitar una">${ICON_MINUS}</button>
          <span>${qty}</span>
          <button type="button" class="bm__stepper-btn" data-action="qty-plus" data-pi="${pi}" data-vi="${vi}" aria-label="Agregar una">${ICON_PLUS}</button>
        </span>`;
    }

    // Nota: /search (Section Rendering) no trae datos de descuento — eso
    // solo existe en el backend del kiosco vía Admin API, fuera de alcance
    // aquí. El precio mostrado es siempre el de catálogo.
    priceHtml(variant, qty) {
      let html = `<span class="bm__price-current">${this.escape(variant.price_formatted)}</span>`;
      if (qty > 1) {
        html += `<span class="bm__price-unit">${this.escape(variant.price_formatted)} c/u</span>`;
      }
      return html;
    }

    // `zoomSrc` hace la miniatura tocable para abrir el visor en grande,
    // igual que `ProductThumb` en el kiosco (abrirImagenGrande).
    thumbHtml(src, alt, cls, zoomSrc) {
      if (src) {
        const zoom = zoomSrc ? ` data-action="zoom" data-src="${this.escape(zoomSrc)}" role="button" tabindex="0" title="Ver imagen en grande"` : '';
        return `<img class="${cls}${zoomSrc ? ' bm__zoomable' : ''}" src="${this.escape(src)}" alt="${this.escape(alt)}" loading="lazy"${zoom}>`;
      }
      const abbr = this.escape((alt || '?').slice(0, 2).toUpperCase());
      return `<div class="${cls} bm__thumb--placeholder" aria-hidden="true">${abbr}</div>`;
    }

    renderGrid(found) {
      this.grid.innerHTML = '';
      found.forEach((entry) => {
        const i = this.entries.indexOf(entry);
        const card = document.createElement('button');
        card.type = 'button';
        card.className = `bm__card bm__gridcard${this.isIncluida(entry) ? '' : ' bm__card--dim'}`;
        card.dataset.index = i;
        card.dataset.action = 'open-modal';
        card.innerHTML = this.renderGridCard(entry);
        this.grid.appendChild(card);
      });
      // El checkbox/remove dentro de la tarjeta no debe abrir el modal.
      this.grid.querySelectorAll('[data-stop-card]').forEach((el) => {
        el.addEventListener('click', (e) => e.stopPropagation());
      });
    }

    renderGridCard(entry) {
      const candidates = this.candidateList(entry);
      const picked = candidates.filter((c) => c.picked);
      const title = picked.length === 1 ? picked[0].product.title : entry.name;
      const thumbSrc = picked[0] ? picked[0].product.image : (entry.products[0] ? entry.products[0].image : null);
      const priceVariant = picked[0] ? picked[0].variant : null;
      const checked = this.isIncluida(entry) ? 'checked' : '';
      const disabled = this.totalPicked(entry) === 0 ? 'disabled' : '';
      return `
        <span class="bm__remove-pill" data-action="remove" data-stop-card role="button" tabindex="0">${ICON_X_SMALL}Quitar</span>
        <div class="bm__gridcard-image">${this.thumbHtml(thumbSrc, title, 'bm__thumb')}</div>
        <div class="bm__gridcard-title">${this.escape(title)}</div>
        <div class="bm__gridcard-price">${priceVariant ? this.escape(priceVariant.price_formatted) : '—'}</div>
        <div class="bm__gridcard-footer">
          <span class="bm__gridcard-progress">${this.totalPicked(entry)}/${entry.qty}</span>
          <label class="bm__row-include" data-stop-card>
            <input type="checkbox" data-action="include" ${checked} ${disabled}>
            Incluir
          </label>
        </div>`;
    }

    renderNotFound(notFound) {
      if (!notFound.length) {
        this.notfoundSection.hidden = true;
        return;
      }
      this.notfoundSection.hidden = false;
      this.notfoundHeading.textContent = `No encontradas (${notFound.length})`;
      this.notfoundList.innerHTML = notFound.map((entry) => {
        const status = this.statusFor(entry);
        const sinStock = entry.productoSinStock;
        const thumb = sinStock && sinStock.image
          ? this.thumbHtml(sinStock.image, sinStock.title, 'bm__notfound-thumb', sinStock.image_large || sinStock.image)
          : `<span class="bm__notfound-thumb" style="display:flex;align-items:center;justify-content:center;color:rgba(0,0,0,.38)">${ICON_ALERT}</span>`;
        return `
          <div class="bm__notfound-row">
            ${thumb}
            <div>
              <div class="bm__notfound-label">${this.escape(status.label)}</div>
              <div class="bm__notfound-raw">${this.escape(entry.raw)}</div>
            </div>
          </div>`;
      }).join('');
    }

    // ---- interacción: lista ----

    onRowClick(e) {
      const row = e.target.closest('.bm__row');
      if (!row) return;
      const entry = this.entries[row.dataset.index];
      const action = e.target.closest('[data-action]');
      if (!action) return;
      this.handleAction(entry, action);
    }

    onRowInput(e) {
      if (e.target.matches('[data-action="include"]')) {
        const row = e.target.closest('.bm__row');
        const entry = this.entries[row.dataset.index];
        entry.touched = true;
        entry.manualIncluida = e.target.checked;
        this.render();
      }
    }

    onGridClick(e) {
      const card = e.target.closest('.bm__gridcard');
      if (!card) return;
      const entry = this.entries[card.dataset.index];
      const action = e.target.closest('[data-action]');
      if (action && action.dataset.action === 'remove') {
        e.stopPropagation();
        this.removeEntry(entry);
        return;
      }
      if (action && action.dataset.action === 'include') {
        e.stopPropagation();
        entry.touched = true;
        entry.manualIncluida = action.checked;
        this.render();
        return;
      }
      if (card.dataset.action === 'open-modal') {
        this.openModal(entry);
      }
    }

    handleAction(entry, actionEl) {
      const action = actionEl.dataset.action;
      const pi = actionEl.dataset.pi !== undefined ? Number(actionEl.dataset.pi) : null;
      const vi = actionEl.dataset.vi !== undefined ? Number(actionEl.dataset.vi) : null;
      if (action === 'zoom') {
        this.openLightbox(actionEl.dataset.src);
        return;
      }
      if (action === 'remove') {
        this.removeEntry(entry);
        return;
      }
      if (action === 'toggle-variants') {
        entry.showVariants = !entry.showVariants;
        entry.variantOrder = null; // al volver a abrir, orden nuevo (elegidas primero)
        this.render();
        return;
      }
      if (action === 'toggle-show-all') {
        entry.showAllVariants = !entry.showAllVariants;
        this.render();
        return;
      }
      if (action === 'qty-minus' || action === 'qty-plus') {
        this.adjustPick(entry, pi, vi, action === 'qty-plus' ? 1 : -1);
        return;
      }
      if (action === 'add-variant') {
        this.addVariant(entry, pi, vi);
      }
    }

    adjustPick(entry, pi, vi, delta) {
      const key = `${pi}:${vi}`;
      const variant = entry.products[pi].variants[vi];
      let qty = clampQty((entry.picks.get(key) || 0) + delta);
      if (variant.quantity !== null && qty > variant.quantity) qty = variant.quantity;
      if (qty > 0) entry.picks.set(key, qty);
      else entry.picks.delete(key);
      entry.touched = false; // una nueva cantidad resetea el override manual de "Incluir"
      this.render();
    }

    addVariant(entry, pi, vi) {
      const variant = entry.products[pi].variants[vi];
      const falta = Math.max(entry.qty - this.totalPicked(entry), 1);
      let qty = falta;
      if (variant.quantity !== null) qty = Math.min(qty, variant.quantity);
      if (qty <= 0) qty = 1;
      entry.picks.set(`${pi}:${vi}`, qty);
      entry.touched = false;
      this.render();
    }

    removeEntry(entry) {
      const idx = this.entries.indexOf(entry);
      if (idx === -1) return;
      this.entries.splice(idx, 1);
      this.render();
    }

    // ---- modal (detalle de una tarjeta de grilla) ----

    openModal(entry) {
      entry.showVariants = true;
      // onModalClick vuelve a llamar openModal tras cada acción para redibujar;
      // el orden de las tarjetas solo se reinicia al abrir el modal de nuevo.
      if (!this.modal.open) entry.variantOrder = null;
      this.modalBody.innerHTML = `
        <div class="bm__card bm__row" style="border:0;padding:0">
          ${this.renderRow(entry, { hideRemove: true })}
        </div>`;
      this.modalBody.dataset.index = this.entries.indexOf(entry);
      if (typeof this.modal.showModal === 'function') this.modal.showModal();
      else this.modal.setAttribute('open', '');
    }

    // Visor en grande (como `abrirImagenGrande` del kiosco): fondo oscuro,
    // la imagen a ~75% del alto de la pantalla, se cierra tocando cualquier
    // parte o con Esc (nativo de <dialog>).
    openLightbox(src) {
      if (!src) return;
      this.lightboxImg.src = src;
      if (typeof this.lightbox.showModal === 'function') this.lightbox.showModal();
      else this.lightbox.setAttribute('open', '');
    }

    closeLightbox() {
      if (typeof this.lightbox.close === 'function') this.lightbox.close();
      else this.lightbox.removeAttribute('open');
      this.lightboxImg.removeAttribute('src');
    }

    closeModal() {
      if (typeof this.modal.close === 'function') this.modal.close();
      else this.modal.removeAttribute('open');
    }

    onModalClick(e) {
      if (e.target === this.modal) { this.closeModal(); return; }
      const index = this.modalBody.dataset.index;
      if (index === undefined) return;
      const entry = this.entries[index];
      if (!entry) return;
      if (e.target.matches('[data-action="include"]')) {
        entry.touched = true;
        entry.manualIncluida = e.target.checked;
        this.render();
        this.openModal(entry);
        return;
      }
      const action = e.target.closest('[data-action]');
      if (!action) return;
      if (action.dataset.action === 'zoom') {
        this.openLightbox(action.dataset.src);
        return;
      }
      this.handleAction(entry, action);
      if (this.entries.includes(entry)) this.openModal(entry);
      else this.closeModal();
    }

    // ---- resumen / footer ----

    updateFooter() {
      let solicitadas = 0;
      let cubiertas = 0;
      let totalCents = 0;
      let lineasAjustadas = 0;
      let lineasNoEncontradas = 0;
      let cartasIncluidas = 0;

      this.entries.forEach((entry) => {
        solicitadas += entry.qty;
        const total = this.totalPicked(entry);
        cubiertas += Math.min(total, entry.qty);
        if (!entry.products.length) {
          lineasNoEncontradas += 1;
          return;
        }
        if (total < entry.qty) lineasAjustadas += 1;
        if (this.isIncluida(entry)) {
          cartasIncluidas += 1;
          entry.picks.forEach((qty, key) => {
            const [pi, vi] = key.split(':').map(Number);
            const variant = entry.products[pi] && entry.products[pi].variants[vi];
            if (!variant) return;
            totalCents += (variant.price || 0) * qty;
          });
        }
      });

      const partes = [`${solicitadas} cartas solicitadas`, `${cubiertas} encontradas`];
      if (lineasAjustadas) partes.push(`${lineasAjustadas} ajustada${lineasAjustadas === 1 ? '' : 's'} a la cantidad disponible`);
      if (lineasNoEncontradas) partes.push(`${lineasNoEncontradas} no encontrada${lineasNoEncontradas === 1 ? '' : 's'} en inventario`);
      const texto = partes.length > 2
        ? `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}.`
        : `${partes.join(', ')}.`;
      this.summaryText.textContent = texto;
      this.summaryTotal.textContent = this.formatMoney(totalCents);

      this.footerWarning.hidden = cartasIncluidas <= 10;
      this.addButton.disabled = totalCents === 0;
    }

    // Copia el formato de `price_formatted` de Shopify (prefijo, sufijo y
    // si el decimal es punto o coma) y agrega el separador de miles que
    // `toFixed` no pone: 198000 → "$ 1,980.00", no "$ 1980.00".
    formatMoney(cents) {
      const sample = (this.entries || []).flatMap((e) => e.products || []).flatMap((p) => p.variants)[0];
      const comaDecimal = !!(sample && /\d,\d{2}\D*$/.test(sample.price_formatted || ''));
      const [entero, dec] = (cents / 100).toFixed(2).split('.');
      const miles = entero.replace(/\B(?=(\d{3})+(?!\d))/g, comaDecimal ? '.' : ',');
      const amount = comaDecimal ? `${miles},${dec}` : `${miles}.${dec}`;
      if (!sample || !sample.price_formatted) return amount;
      const prefix = sample.price_formatted.match(/^[^\d]*/)[0];
      const suffix = sample.price_formatted.match(/[^\d]*$/)[0];
      return `${prefix}${amount}${suffix}`;
    }

    collectPicks() {
      const picks = [];
      this.entries.forEach((entry) => {
        if (!this.isIncluida(entry)) return;
        entry.picks.forEach((qty, key) => {
          const [pi, vi] = key.split(':').map(Number);
          const product = entry.products[pi];
          const variant = product && product.variants[vi];
          if (!variant || qty <= 0) return;
          picks.push({ entry, key, product, variant, qty });
        });
      });
      return picks;
    }

    // Agrega directo al carrito, igual que el kiosco (pedido 2026-10-22: se
    // quitó el paso "Revisa antes de agregar" — la barra flotante ya muestra
    // el total y lo solicitado/encontrado antes de tocar el botón).
    onAddToCartClick() {
      const picks = this.collectPicks();
      this.cartMessage.hidden = true;
      if (!picks.length) {
        this.cartMessage.hidden = false;
        this.cartMessage.textContent = 'No hay cartas marcadas para incluir.';
        return;
      }
      this.addToCart(picks);
    }

    async postCartAdd(items, sections) {
      const res = await fetch(`${this.dataset.cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
      });
      const data = await res.json().catch(() => ({}));
      return { ok: res.ok, data };
    }

    async addToCart(picks) {
      const lines = [];
      const shorted = [];
      picks.forEach((pick) => {
        const { entry, variant } = pick;
        let qty = pick.qty;
        if (variant.quantity !== null && variant.quantity > 0 && qty > variant.quantity) {
          shorted.push(`${entry.name} (${variant.quantity})`);
          qty = variant.quantity;
        }
        if (qty > 0) lines.push({ pick, item: { id: variant.id, quantity: qty } });
      });

      this.cartMessage.hidden = false;
      if (!lines.length) {
        this.cartMessage.textContent = 'No hay cartas seleccionadas.';
        return;
      }

      this.addButton.disabled = true;
      this.cartMessage.textContent = 'Agregando al carrito…';
      try {
        const bundleDetail = { sections: [] };
        document.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: bundleDetail }));

        // `/cart/add.js` rechaza el lote COMPLETO si una sola línea no se
        // puede agregar (agotada o ya al máximo en el carrito) — pedido
        // 2026-10-27: que entren las demás. Se intenta el lote; si falla,
        // se agrega línea por línea y solo se reportan las que no entraron.
        let { ok, data } = await this.postCartAdd(lines.map((l) => l.item), bundleDetail.sections);
        const failed = [];
        if (!ok) {
          let lastData = null;
          for (const line of lines) {
            const r = await this.postCartAdd([line.item], bundleDetail.sections);
            if (r.ok) lastData = r.data;
            else failed.push({ line, reason: r.data.description || r.data.message || 'no disponible' });
          }
          if (failed.length === lines.length) {
            throw new Error(failed.length === 1
              ? `${failed[0].line.pick.entry.name}: ${failed[0].reason}`
              : 'No se pudo agregar ninguna carta: ya no hay existencias o ya tienes la cantidad máxima en el carrito.');
          }
          data = lastData || {};
        }
        const failedPicks = new Set(failed.map((f) => f.line.pick));

        // Cada tema refresca su carrito distinto — se detecta cuál hay:
        // - Concept (RoarTheme): pub/sub propio + <cart-drawer>.
        // - Premium Theme 4 (BinderPOS): función global jQuery
        //   adjustCartDropDown() (assets/shop.js.liquid) que relee /cart.js
        //   y redibuja `.cart-qty` y el mini-cart; el dropdown es un
        //   collapse de Bootstrap 3 (`.cart-container`).
        if (window.theme && window.theme.pubsub && window.theme.pubsub.PUB_SUB_EVENTS) {
          const cartRes = await fetch(`${this.dataset.cartUrl}.js`);
          const cartJson = await cartRes.json();
          cartJson.sections = data.sections || {};
          window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'busqueda-multiple',
            cart: cartJson,
          });
          document.querySelector('cart-drawer')?.show(this.addButton);
        } else if (typeof window.adjustCartDropDown === 'function') {
          window.adjustCartDropDown();
          if (window.jQuery && window.jQuery.fn && window.jQuery.fn.collapse) {
            window.jQuery('#_desktop_cart .cart-container').collapse('show');
          }
        }

        let msg = 'Cartas agregadas al carrito.';
        if (shorted.length) msg += ` Cantidades ajustadas a la existencia disponible: ${shorted.join(', ')}.`;
        if (failed.length) {
          const nombres = [...new Set(failed.map((f) => f.line.pick.entry.name))];
          msg += ` ${nombres.length === 1 ? 'No se pudo' : 'No se pudieron'} agregar ${nombres.join(', ')}: ya no hay existencias o ya tienes la cantidad máxima en el carrito.`;
        }
        this.cartMessage.innerHTML =
          `${failed.length ? ICON_WARN : ICON_OK} ${this.escape(msg)} <a href="${this.escape(this.dataset.cartUrl)}">Ver carrito</a>`;
        // Lo agregado sale de la lista para no duplicarlo si se vuelve a
        // tocar "Agregar al carrito"; lo que falló se queda para que se vea.
        const addedEntries = new Set();
        lines.forEach(({ pick }) => {
          if (failedPicks.has(pick)) return;
          addedEntries.add(pick.entry);
          pick.entry.picks.delete(pick.key);
        });
        this.entries = this.entries.filter((e) => !addedEntries.has(e) || e.picks.size > 0);
        if (this.entries.length) this.render();
        else this.resultsEl.hidden = true;
      } catch (err) {
        this.cartMessage.innerHTML = `${ICON_WARN} ${this.escape(err.message)}`;
      } finally {
        this.addButton.disabled = false;
      }
    }

    escape(str) {
      const div = document.createElement('div');
      div.textContent = String(str);
      return div.innerHTML.replace(/"/g, '&quot;');
    }
  }

  if (!customElements.get('busqueda-multiple')) {
    customElements.define('busqueda-multiple', BusquedaMultiple);
  }
})();
